import type { ReactNode } from 'react';
import { Children } from 'react';
import classnames from 'classnames';

type ButtonGroupProps = {
  spacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  direction?: 'left' | 'right';
  children: ReactNode[];
};

//  Determine if it's the last node
function isLastChild(children: ReactNode[], index: number): boolean {
  return Children.count(children) === (index + 1);
}

/**
 * Used to render an inline row of buttons with consistent spacing between.
 *
 * @param {ButtonGroupProps} props - ButtonGroup props
 * @param {'xs' | 'sm' | 'md' | 'lg' | 'xl'} [props.spacing] - Spacing size to use (default: 'sm').
 * @param {'left' | 'right'} [props.direction] - Direction to apply the margins to each button (default: 'right').
 * @param {ReactNode[]} props.children - <Button /> components (must be more than one).
 */
const ButtonGroup = ({
  spacing = 'sm',
  direction = 'right',
  children,
}: ButtonGroupProps) => (
  <div className="fossa-button-group">
    {Children.map(children, (child, index) => !child
      ? null
      : (
        <span className={
          classnames('inline-block', {
            [`margin-${direction}-${spacing}`]: !isLastChild(children, index),
          })
        }>
          {child}
        </span>
        ))}
  </div>
);

export default ButtonGroup;
